import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, Button, Paper, Popper, Portal, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import type { PopperPlacementType } from '@mui/material/Popper'

export interface FeatureDemoStep {
  title: string
  body: string
}

interface FeatureDemoProps {
  open: boolean
  anchorEl: HTMLElement | null
  title: string
  body: string
  stepLabel?: string
  primaryLabel: string
  secondaryLabel?: string
  onPrimary: () => void
  onSecondary?: () => void
  tertiaryLabel?: string
  onTertiary?: () => void
  /** Writes a permanent hide. Small text in the top right of the card. */
  onDismissForever?: () => void
  placement?: PopperPlacementType
}

interface HoleRect {
  top: number
  left: number
  width: number
  height: number
}

const HOLE_PAD = 6

function useAnchorHole(anchorEl: HTMLElement | null, open: boolean): HoleRect | null {
  const [hole, setHole] = useState<HoleRect | null>(null)

  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setHole(null)
      return undefined
    }

    const update = () => {
      const rect = anchorEl.getBoundingClientRect()
      setHole({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      })
    }

    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    const observer = new ResizeObserver(update)
    observer.observe(anchorEl)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      observer.disconnect()
    }
  }, [open, anchorEl])

  return hole
}

export function FeatureDemo({
  open,
  anchorEl,
  title,
  body,
  stepLabel,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  tertiaryLabel,
  onTertiary,
  onDismissForever,
  placement,
}: FeatureDemoProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const hole = useAnchorHole(anchorEl, open)
  const primaryRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (open) primaryRef.current?.focus()
  }, [open, title])

  const padded = hole
    ? {
        top: hole.top - HOLE_PAD,
        left: hole.left - HOLE_PAD,
        right: hole.left + hole.width + HOLE_PAD,
        bottom: hole.top + hole.height + HOLE_PAD,
        width: hole.width + HOLE_PAD * 2,
        height: hole.height + HOLE_PAD * 2,
      }
    : null

  return (
    <>
      {open && (
        <Portal>
          <Box
            aria-hidden='true'
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
              pointerEvents: 'none',
            }}
          >
            {padded ? (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: Math.max(0, padded.top),
                    bgcolor: 'rgba(15, 23, 42, 0.48)',
                    pointerEvents: 'auto',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: padded.bottom,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(15, 23, 42, 0.48)',
                    pointerEvents: 'auto',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: padded.top,
                    left: 0,
                    width: Math.max(0, padded.left),
                    height: padded.height,
                    bgcolor: 'rgba(15, 23, 42, 0.48)',
                    pointerEvents: 'auto',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: padded.top,
                    left: padded.right,
                    right: 0,
                    height: padded.height,
                    bgcolor: 'rgba(15, 23, 42, 0.48)',
                    pointerEvents: 'auto',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: padded.top,
                    left: padded.left,
                    width: padded.width,
                    height: padded.height,
                    borderRadius: 2,
                    boxShadow: (muiTheme) => `0 0 0 2px ${muiTheme.palette.primary.main}`,
                    pointerEvents: 'none',
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  bgcolor: 'rgba(15, 23, 42, 0.48)',
                  pointerEvents: 'auto',
                }}
              />
            )}
          </Box>
        </Portal>
      )}
      <Popper
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement={placement || (isMobile ? 'bottom-start' : 'bottom')}
        modifiers={[
          { name: 'offset', options: { offset: [0, 14] } },
          { name: 'flip', enabled: true },
          { name: 'preventOverflow', options: { padding: 16, altAxis: true } },
        ]}
        sx={{ zIndex: (muiTheme) => muiTheme.zIndex.modal + 2, maxWidth: 'min(320px, calc(100vw - 32px))' }}
      >
        <Paper
          role='dialog'
          aria-labelledby='feature-demo-title'
          aria-describedby='feature-demo-body'
          data-testid='feature-demo'
          elevation={8}
          sx={{
            p: 2.5,
            borderRadius: 2,
            overscrollBehavior: 'contain',
          }}
        >
          {(stepLabel || onDismissForever) && (
            <Stack
              direction='row'
              alignItems='flex-start'
              justifyContent='space-between'
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              {stepLabel ? (
                <Typography variant='caption' color='text.secondary' sx={{ pt: '1px' }}>
                  {stepLabel}
                </Typography>
              ) : (
                <Box />
              )}
              {onDismissForever ? (
                <Button
                  onClick={onDismissForever}
                  data-testid='feature-demo-dismiss-forever'
                  sx={{
                    p: 0,
                    minWidth: 0,
                    minHeight: 0,
                    fontSize: '0.65rem',
                    lineHeight: 1.2,
                    textTransform: 'none',
                    color: 'text.disabled',
                    flexShrink: 0,
                    textAlign: 'right',
                  }}
                >
                  Don&apos;t show me this again
                </Button>
              ) : null}
            </Stack>
          )}
          <Typography
            id='feature-demo-title'
            variant='subtitle1'
            fontWeight={700}
            sx={{ textWrap: 'balance', mb: 1 }}
          >
            {title}
          </Typography>
          <Typography id='feature-demo-body' variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {body}
          </Typography>
          <Stack direction='row' spacing={1} justifyContent='flex-end' flexWrap='wrap' useFlexGap>
            {tertiaryLabel && onTertiary && (
              <Button
                onClick={onTertiary}
                color='inherit'
                sx={{ minHeight: 44, touchAction: 'manipulation', textTransform: 'none' }}
              >
                {tertiaryLabel}
              </Button>
            )}
            {secondaryLabel && onSecondary && (
              <Button
                onClick={onSecondary}
                sx={{ minHeight: 44, touchAction: 'manipulation', textTransform: 'none' }}
              >
                {secondaryLabel}
              </Button>
            )}
            <Button
              ref={primaryRef}
              variant='contained'
              onClick={onPrimary}
              data-testid='feature-demo-primary'
              sx={{ minHeight: 44, minWidth: 88, touchAction: 'manipulation', textTransform: 'none' }}
            >
              {primaryLabel}
            </Button>
          </Stack>
        </Paper>
      </Popper>
    </>
  )
}
